import { create } from "zustand";

export type InstanceCategory =
  | "All Types"
  | "General Purpose"
  | "Compute Purpose"
  | "Memory Purpose";

export type ColumnKey =
  | "instanceType"
  | "instanceFamily"
  | "vcpus"
  | "memory"
  | "cpuArchitecture"
  | "gpu"
  | "gpuName"
  | "aws"
  | "gcp"
  | "azure"
  | "best";

export type ColumnVisibility = Record<ColumnKey, boolean>;

interface FilterState {
  region: string;
  pricingModel: string;
  operatingSystem: string;
  timeUnit: string;
  category: InstanceCategory;
  searchQuery: string;
  vcpuRange: [number, number];
  memoryRange: [number, number];
  priceRange: [number, number];
  columnVisibility: ColumnVisibility;
  cpuArchitecture: string;

  setRegion: (region: string) => void;
  setPricingModel: (pricingModel: string) => void;
  setOperatingSystem: (os: string) => void;
  setTimeUnit: (timeUnit: string) => void;
  setCategory: (category: InstanceCategory) => void;
  setSearchQuery: (query: string) => void;
  setVcpuRange: (range: [number, number]) => void;
  setMemoryRange: (range: [number, number]) => void;
  setPriceRange: (range: [number, number]) => void;
  setColumnVisibility: (column: ColumnKey, visible: boolean) => void;
  toggleColumnVisibility: (column: ColumnKey) => void;
  resetColumnVisibility: () => void;
  setCpuArchitecture: (arch: string) => void;
  resetFilters: () => void;
}

const defaultColumnVisibility: ColumnVisibility = {
  instanceType: true,
  instanceFamily: true,
  vcpus: true,
  memory: true,
  cpuArchitecture: true,
  gpu: false,
  gpuName: false,
  aws: true,
  gcp: true,
  azure: true,
  best: true,
};

export const useFilterStore = create<FilterState>((set) => ({
  region: "",
  pricingModel: "",
  operatingSystem: "",
  timeUnit: "Per Hour",
  category: "All Types",
  searchQuery: "",
  vcpuRange: [1, 64],
  memoryRange: [1, 256],
  priceRange: [0, 100],
  columnVisibility: defaultColumnVisibility,
  cpuArchitecture: "",

  setRegion: (region) => set({ region }),
  setPricingModel: (pricingModel) => set({ pricingModel }),
  setOperatingSystem: (operatingSystem) => set({ operatingSystem }),
  setTimeUnit: (timeUnit) => set({ timeUnit }),
  setCategory: (category) => set({ category }),
  setSearchQuery: (searchQuery) => set({ searchQuery }),
  setVcpuRange: (vcpuRange) => set({ vcpuRange }),
  setMemoryRange: (memoryRange) => set({ memoryRange }),
  setPriceRange: (priceRange) => set({ priceRange }),
  setColumnVisibility: (column, visible) =>
    set((state) => ({
      columnVisibility: {
        ...state.columnVisibility,
        [column]: visible,
      },
    })),
  toggleColumnVisibility: (column) =>
    set((state) => ({
      columnVisibility: {
        ...state.columnVisibility,
        [column]: !state.columnVisibility[column],
      },
    })),
  resetColumnVisibility: () =>
    set({ columnVisibility: {
      instanceType: true,
      instanceFamily: true,
      vcpus: true,
      memory: true,
      cpuArchitecture: true,
      gpu: true,
      gpuName: true,
      aws: true,
      gcp: true,
      azure: true,
      best: true,
    } }),
  setCpuArchitecture: (cpuArchitecture) => set({ cpuArchitecture }),
  resetFilters: () =>
    set({
      region: "",
      pricingModel: "",
      operatingSystem: "",
      timeUnit: "",
      category: "All Types",
      searchQuery: "",
      vcpuRange: [1, 64],
      memoryRange: [1, 256],
      priceRange: [0, 100],
      columnVisibility: defaultColumnVisibility,
      cpuArchitecture: "",
    }),
}));
